import { useEffect, useMemo, useRef, useState } from 'react';
import { buildTooltipHtml, getConstituencyById } from './constituencyTooltip';

const SVG_PATH = '/img/svg/indiaStateConstitiency2024.svg';

function IndiaConstituencyMap() {
  const [data, setData] = useState([]);
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0, html: '' });
  const [svgMarkup, setSvgMarkup] = useState('');
  const mapRef = useRef(null);

  useEffect(() => {
    fetch('/json/statesConstituency2024.json')
      .then((response) => response.json())
      .then((records) => setData(records))
      .catch(() => setData([]));

    fetch(SVG_PATH)
      .then((response) => response.text())
      .then((markup) => setSvgMarkup(markup))
      .catch(() => setSvgMarkup(''));
  }, []);

  const lookup = useMemo(() => {
    return new Map(data.map((item) => [String(item.constCode).toLowerCase(), item]));
  }, [data]);

  useEffect(() => {
    if (!svgMarkup || !mapRef.current) {
      return;
    }

    const container = mapRef.current;
    container.innerHTML = svgMarkup;

    const svg = container.querySelector('svg');
    if (!svg) {
      return;
    }

    svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
    svg.style.width = '100%';
    svg.style.height = 'auto';
    svg.style.display = 'block';

    const allShapes = Array.from(svg.querySelectorAll('[id]'));

    allShapes.forEach((element) => {
      const constCode = String(element.id || '').trim();
      const record = lookup.get(constCode.toLowerCase()) || getConstituencyById(data, constCode);

      if (!record) {
        return;
      }

      element.style.cursor = 'pointer';
      element.style.transition = 'fill 0.15s ease';

      const showTooltip = (event) => {
        const html = buildTooltipHtml(record);
        if (!html) {
          return;
        }

        const containerRect = container.getBoundingClientRect();
        const x = event.clientX - containerRect.left + 18;
        const y = event.clientY - containerRect.top + 18;

        setTooltip({ visible: true, x, y, html });
      };

      const hideTooltip = () => setTooltip((current) => ({ ...current, visible: false }));

      element.addEventListener('mouseenter', showTooltip);
      element.addEventListener('mousemove', showTooltip);
      element.addEventListener('mouseleave', hideTooltip);
      element.addEventListener('focus', showTooltip);
      element.addEventListener('blur', hideTooltip);

      const baseFill = element.getAttribute('fill');
      if (!baseFill || baseFill === '#FFFFFF' || baseFill === 'white') {
        element.setAttribute('fill', '#dbeafe');
      }

      element.addEventListener('mouseover', () => {
        const currentFill = element.getAttribute('fill');
        if (currentFill && currentFill !== '#f59e0b') {
          element.setAttribute('data-prev-fill', currentFill);
        }
        element.setAttribute('fill', '#f59e0b');
      });

      element.addEventListener('mouseout', () => {
        const previousFill = element.getAttribute('data-prev-fill');
        element.setAttribute('fill', previousFill || baseFill || '#dbeafe');
      });
    });

    return () => {
      setTooltip({ visible: false, x: 0, y: 0, html: '' });
    };
  }, [data, lookup, svgMarkup]);

  return (
    <div className="india-map-wrapper">
      <div ref={mapRef} className="india-map" aria-label="India constituency map" />
      {tooltip.visible && (
        <div
          className="constituency-tooltip-box"
          dangerouslySetInnerHTML={{ __html: tooltip.html }}
          style={{ left: tooltip.x, top: tooltip.y }}
        />
      )}
    </div>
  );
}

export default IndiaConstituencyMap;
